import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import reportWebVitals from './reportWebVitals'
import axios from 'axios'
import { createTheme, ThemeProvider } from '@mui/material'
import { createBrowserRouter, RouterProvider } from 'react-router'
import routes from './routes'
import { loginRequest, msalConfig } from './authConfig'
import { BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DataProvider } from './hooks'

declare module '@mui/material/Checkbox' {
  interface CheckboxProps {
    displayOnly?: boolean
  }
}

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL
dayjs.extend(utc)

const msalInstance = new PublicClientApplication(msalConfig)
const router = createBrowserRouter(routes)

let inflightTokenRequest: Promise<string> | null = null

async function getAccessToken() {
  if (inflightTokenRequest) return inflightTokenRequest

  inflightTokenRequest = (async () => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        account: msalInstance.getActiveAccount() ?? undefined,
      })
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        })
        return response.accessToken
      }
      throw error
    }
  })()

  try {
    return await inflightTokenRequest
  } finally {
    inflightTokenRequest = null
  }
}

axios.interceptors.request.use(
  async config => {
    config.headers.Authorization = await getAccessToken()
    return config
  },
  error => {
    return Promise.reject(error)
  },
)

const palette = createTheme({
  palette: {
    primary: {
      main: '#32578b',
      contrastText: '#FFFFFF',
    },
  },
})

const theme = createTheme({
  cssVariables: true,
  typography: {
    fontFamily: 'Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'white',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { displayOnly: true },
              style: {
                '&.Mui-disabled': {
                  color: 'var(--mui-palette-text-secondary)',
                  '&.Mui-checked': {
                    color: 'var(--mui-palette-primary-main)',
                  },
                },
              },
            },
          ],
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiDialogContent-root > .MuiTextField-root:first-child, & .MuiDialogContent-root > .MuiAutocomplete-root':
            {
              marginTop: '8px',
            },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiSelect-select': {
            background: 'white',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { color: 'primary' },
              style: {
                '& .MuiTableHead-root .MuiTableCell-root': {
                  backgroundColor: palette.palette.primary.main,
                  color: palette.palette.primary.contrastText,
                },
                '& .MuiTableHead-root .MuiTableSortLabel-root, & .MuiTableHead-root .MuiTableSortLabel-root svg, & .MuiTableHead-root .MuiIconButton-root':
                  {
                    color: palette.palette.primary.contrastText,
                  },
                '& .MuiTableHead-root .MuiCheckbox-root': {
                  color: palette.palette.primary.contrastText,
                },
              },
            },
            {
              props: { color: 'secondary' },
              style: {
                '& .MuiTableHead-root .MuiTableCell-root': {
                  backgroundColor: '#f5f5f5',
                },
              },
            },
          ],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: '4px',
          paddingBottom: '4px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        list: {
          height: '100%',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            background: 'white',
          },
        },
      },
    },
    ...({
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            background: 'white',
          },
        },
      },
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            '& .MuiPickersInputBase-root': {
              background: 'white',
            },
          },
        },
      },
    } as Record<string, unknown>),
  },
  palette: palette.palette,
})

function App() {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState<string | null>(null)

  useEffect(() => {
    if (inProgress !== 'none') {
      return
    }

    // TODO: DATADOG
    if (isAuthenticated) {
      instance.setActiveAccount(instance.getAllAccounts()[0])

      if (!email) {
        setEmail(instance.getActiveAccount()?.username ?? '')
        setDisplayName(instance.getActiveAccount()?.name ?? null)
      }
    } else {
      instance.loginRedirect(loginRequest).catch(e => {
        console.log(e)
      })

      instance.handleRedirectPromise().then(() => {
        if (instance.getAllAccounts().length > 0) {
          instance.setActiveAccount(instance.getAllAccounts()[0])
          setEmail(instance.getActiveAccount()?.username ?? '')
          setDisplayName(instance.getActiveAccount()?.name ?? null)
        }
      })
    }
  }, [inProgress, isAuthenticated, instance])

  return isAuthenticated ? (
    <DataProvider email={email} displayName={displayName}>
      <RouterProvider router={router} />
    </DataProvider>
  ) : (
    <div>
      <p>Please sign in</p>
    </div>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <App />
      </ThemeProvider>
    </MsalProvider>
  </React.StrictMode>,
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
